/** Editor registry — teardown after React unmounts Shell, never during layout commit. */

import { isNotebookDebugEnabled, nbLog, nbWarn, scanNotebookDom } from './notebook-debug'

/** @type {Set<import('@tiptap/react').Editor>} */
const editors = new Set()

let orphanCheckFrame = null

export function registerNotebookEditor(editor) {
  if (!editor || editor.isDestroyed) return () => {}
  editors.add(editor)
  return () => {
    editors.delete(editor)
  }
}

export function destroyAllNotebookEditors() {
  const count = editors.size
  if (isNotebookDebugEnabled()) {
    nbLog('editor', 'destroyAllNotebookEditors', { count })
  }
  for (const editor of [...editors]) {
    try {
      if (editor && !editor.isDestroyed) editor.destroy()
    } catch {
      /* ignore teardown races */
    }
    editors.delete(editor)
  }
}

/** Portaled UI only — ProseMirror is removed by editor.destroy() + React unmount. */
export function purgePortaledEditorUi() {
  if (typeof document === 'undefined') return
  document.querySelectorAll('[data-tippy-root]').forEach((node) => node.remove())
}

function scheduleOrphanDomCheck(phase) {
  if (typeof window === 'undefined' || !isNotebookDebugEnabled()) return

  if (orphanCheckFrame != null) {
    cancelAnimationFrame(orphanCheckFrame)
  }

  orphanCheckFrame = requestAnimationFrame(() => {
    orphanCheckFrame = null
    const after = scanNotebookDom()
    const disconnectedOrphans = (after.orphanProseMirror ?? []).filter(
      (p) => !p.connected,
    )
    const overlaps = after.richWrapperOverlaps ?? []

    if (disconnectedOrphans.length > 0) {
      nbWarn(`Disconnected ProseMirror after ${phase}`, disconnectedOrphans)
    }
    if (overlaps.length > 0) {
      nbWarn(`Overlapping rich wrappers after ${phase}`, overlaps)
    }
    if (after.possibleDuplicateEditors) {
      nbWarn(`Possible duplicate editors after ${phase}`, after)
    }
  })
}

export function resetNotebookEditorSurface() {
  if (isNotebookDebugEnabled()) {
    nbLog('editor', 'resetNotebookEditorSurface START', scanNotebookDom())
  }
  destroyAllNotebookEditors()
  purgePortaledEditorUi()
  if (isNotebookDebugEnabled()) {
    nbLog('editor', 'resetNotebookEditorSurface DONE', scanNotebookDom())
  }
  scheduleOrphanDomCheck('resetNotebookEditorSurface')
}
